<template>
  <div class="modal modal--product-form fade" :id="modalId" tabindex="-1" :aria-labelledby="`${modalId}Label`" aria-hidden="true" v-if="product">
    <div class="modal-dialog modal-dialog-centered modal-lg" :key="`modal-product-${product.id}`">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" :id="`${modalId}Label`">{{ product.name }}</h5>
          <button type="button" class="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <form class="product-form" @submit.prevent="() => isNew ? addItem() : updateItem()">
            <div class="row">
              <div :class="`col-12 ${hasFractions || hasAdditionals || hasObservations ? 'col-md-6' : ''}`">
                <template v-if="product.images && Array.isArray(product.images)">
                  <div v-if="product.images.length > 1" id="productImagesSlides" class="carousel slide mb-3"
                    data-ride="carousel">
                    <div class="carousel-inner">
                      <div v-for="(image, index) in product.images" :key="index"
                        :class="`carousel-item ${index === 0 ? 'active' : ''}`">
                        <secure-image :src="image" class="d-block w-100 img-fluid" :default-src="product.default_image_url"></secure-image>
                      </div>
                    </div>
                    <a class="carousel-control-prev" href="#productImagesSlides" role="button" data-slide="prev">
                      <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                      <span class="sr-only">{{ $t('previous') }}</span>
                    </a>
                    <a class="carousel-control-next" href="#productImagesSlides" role="button" data-slide="next">
                      <span class="carousel-control-next-icon" aria-hidden="true"></span>
                      <span class="sr-only">{{ $t('next') }}</span>
                    </a>
                  </div>
                  <figure v-else class="product-image mb-3">
                    <secure-image :src="product.images[0]" :default-src="product.default_image_url" alt="No Encontrada"></secure-image>
                  </figure>
                </template>

                <p class="product__description" v-if="product.description && product.description.toLowerCase() !== 'no aplica'">{{ product.description }}</p>
              </div>

              <div v-if="hasFractions || hasAdditionals || hasObservations" class="col-12 col-md-6">
                <div v-if="hasFractions" class="mb-3">
                  <h5>{{ $t('product.presentation') }}</h5>

                  <select class="custom-select" v-model="product.fraction_id">
                    <option v-for="fraction in product.fractions" :key="fraction.id" :value="fraction.id">{{
                      `${convertToCurrency(fraction.price)} x ${fraction.description} ${(fraction.quantity > 1 ?
                        fraction.quantity + ' ' + $t('units') : '')}` }}</option>
                  </select>
                </div>

                <div v-if="hasAdditionals" class="mb-3">
                  <h5>{{ additionalsTitle }}</h5>

                  <table class="table table-borderless table-hover mb-0 w-100">
                    <tbody>
                      <tr v-for="additional in additionals" :key="`additional-${additional.id}`"
                        style="vertical-align: center;">
                        <td>
                          <div class="custom-control custom-checkbox product__additional">
                            <input :disabled="reachedMaxAdditionals && !additional.checked" type="checkbox"
                              @click="toggleAdditional(additional.checked)" class="custom-control-input"
                              :id="`additional-${additional.id}`" v-model="additional.checked">
                            <label class="custom-control-label w-100" :for="`additional-${additional.id}`">{{ additional.name.toLowerCase() }}</label>
                          </div>
                        </td>
                        <td class="text-right text-muted mb-2" style="min-width: 100px;">
                          +{{ convertToCurrency(additional.price) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="hasObservations" class="mb-3">
                  <h5>{{ $t('observation.list') }}</h5>

                  <table class="table table-borderless table-hover mb-0 w-100">
                    <tbody>
                      <tr v-for="observation in observations" :key="`observation-${observation.id}`">
                        <td>
                          <div class="custom-control custom-checkbox product__observation">
                            <input type="checkbox" class="custom-control-input" :id="`observation-${observation.id}`"
                              v-model="observation.checked">
                            <label class="custom-control-label w-100" :for="`observation-${observation.id}`">{{
                              observation.name.toLowerCase() }}</label>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <footer class="product-form__footer">
              <div class="product-form__footer-quantity-and-total">
                <div class="input-group product-form__footer-quantity">
                  <div class="input-group-prepend">
                    <button class="btn btn-sm btn-secondary" :title="$t('action.remove')"
                      @click.prevent="decrementQuantity" type="button"><span class="material-icons">remove</span></button>
                  </div>
                  <input type="number" class="custom-quantity-field" min="1" :max="maximumQuantity"
                    :aria-label="$t('activerecord.attributes.order_detail.quantity')" v-model="quantity" ref="quantityField" readonly required />
                  <div class="input-group-append">
                    <button class="btn btn-sm btn-secondary" :title="$t('action.add')" type="button"
                      @click.prevent="incrementQuantity"><span class="material-icons">add</span></button>
                  </div>
                </div>

                <h5 class="flex-grow">{{ $t('total') }} {{ convertToCurrency(total) }}</h5>
              </div>

              <button type="submit" class="btn btn-primary product-form__footer-submit"
                :title="$t('action.add_to_cart')">
                <span class="material-icons mr-1">add_shopping_cart</span>
                <span class="d-none d-sm-inline mr-1">{{ isNew ? $t('action.add_to_cart') : $t('action.update') }}</span>
              </button>
            </footer>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wemeikPopup } from '../core'
import SecureImage from './SecureImage'
import { convertToCurrency, isTheMaximumQuantity } from '../../utils/functions'
import additionalMixin from '../mixins/additionals'

export default {
  props: {
    modalId: { type: String, required: false, default: 'itemFormModal' },
    initItem: { type: Object, required: false },
    isNew: { type: Boolean, required: false, default: true }
  },
  data () {
    return {
      additionals: [],
      hasAdditionals: false,
      hasFractions: false,
      hasObservations: false,
      observations: [],
      quantity: 1,
      maximumQuantity: 100,
      unitPrice: 0,
      product: {}
    }
  },
  components: { 'secure-image': SecureImage },
  mixins: [additionalMixin],
  mounted () {
    this.setProduct(this.initItem)
  },
  watch: {
    initItem: {
      deep: true,
      handler () {
        this.additionalsCount = 0
        this.setProduct(this.initItem)
        this.recalculateAdditionals()
      }
    },
    quantity: {
      handler () {
        if (!this.quantity || parseFloat(this.quantity) < 1) {
          this.quantity = 1
        }
      }
    }
  },
  computed: {
    total () {
      let total = 0
      let unitPrice = parseFloat(this.unitPrice)

      if (this.hasFractions) {
        const selectedFraction = this.product.fractions.find((fraction) => fraction.id === this.product.fraction_id)
        unitPrice = parseFloat(selectedFraction.price)
      }

      const quantity = parseFloat(this.quantity)
      total = quantity * unitPrice

      if (this.hasAdditionals) {
        const additionalsTotal = quantity * this.additionals.reduce((accumulator, a) => accumulator + (a.checked ? parseFloat(a.price) : 0), 0)
        total += additionalsTotal
      }

      return total
    }
  },
  methods: {
    convertToCurrency: convertToCurrency,
    setProduct (product) {
      this.product = Object.assign({}, this.product, product)
      this.additionals = product.additionals
      this.observations = product.observations
      this.maximumQuantity = product.maximum_quantity > 0 ? product.maximum_quantity : this.maximumQuantity
      this.product.max_additionals = product.max_additionals === 0 ? product.additionals.length : product.max_additionals

      this.hasAdditionals = Array.isArray(this.product.additionals) && this.product.additionals.length > 0
      this.hasFractions = Array.isArray(this.product.fractions) && this.product.fractions.length > 0
      this.hasObservations = Array.isArray(this.product.observations) && this.product.observations.length > 0

      this.unitPrice = parseFloat(this.product.price)
      if (this.hasFractions && !this.product.fraction_id) {
        this.product.fraction_id = this.product.fractions[0].id
        this.unitPrice = parseFloat(this.product.fractions[0].price)
      }

      this.quantity = parseFloat(product.quantity) > 0 ? parseFloat(product.quantity) : 1
    },
    decrementQuantity (e) {
      if (this.quantity > 1) {
        this.quantity = this.quantity - 1
      }
    },
    incrementQuantity (e) {
      if (!isTheMaximumQuantity(this.quantity, this.maximumQuantity)) {
        this.quantity = this.quantity + 1
      }
    },
    isValid () {
      if (this.quantity <= 0) {
        wemeikPopup({
          title: this.$t('alert.warning'),
          text: this.$t('item.warnings.quantity_less_than_or_equal_to_zero'),
          icon: 'warning'
        })
          .then(() => {
            this.$refs.quantityField.focus()
          })

        return false
      }

      if (this.additionalsCount < this.product.min_additionals) {
        wemeikPopup({
          title: this.$t('alert.warning'),
          text: this.$t('additional.warnings.quantity_more_than_or_equal_to') + this.product.min_additionals,
          icon: 'warning'
        })
          .then(() => {
            this.$refs.quantityField.focus()
          })

        return false
      }

      return true
    },
    prepareProductForCart () {
      return Object.assign({}, this.product, {
        additionals: this.additionals,
        observations: this.observations,
        product_id: this.product.id,
        price: this.unitPrice,
        quantity: this.quantity,
        total: this.total
      })
    },
    addItem (e) {
      if (!this.isValid()) {
        return
      }

      this.$emit('add', this.prepareProductForCart())
    },
    updateItem () {
      if (!this.isValid()) {
        return
      }

      this.$emit('update', this.prepareProductForCart())
    }
  }
}
</script>
